import MonacoEditor from "@monaco-editor/react";
import { Button, Dropdown, MenuProps, Modal, Tabs } from "antd";
import { TabsProps } from "antd/lib";
import { makeAutoObservable } from "mobx";
import { observer } from "mobx-react-lite";
import * as React from "react";
import { ZAttrMeta, zAttrMeta } from "src/business/attribute";
import { getErrorMessage } from "src/common/onError";
import YAML from "yaml";
import { ZodError } from "zod";

import { AttrFeatures } from "./AttrFeatures";
import { AttrMetaEditorForm } from "./AttrMetaEditorForm";
import { presets } from "./presets";

type PropsAttrMeatEditorModal = {
  value: ZAttrMeta | undefined;
  close(): void;
  onChange(updated: ZAttrMeta): void;
  attrFeatures?: AttrFeatures;
};

const getZodErrorText = (e: ZodError): string => {
  const { issues, message } = e;
  const item = issues?.[0];
  if (!item) {
    return message;
  }
  let msg = item.message;
  if (item.path) msg = `${msg} IN ${item.path.join(".")}`;
  return msg;
};

type Format = "edit" | "json" | "yaml";

class AttrMetaStore {
  constructor(
    public srcData: ZAttrMeta,
    public attrFeatures?: AttrFeatures,
  ) {
    makeAutoObservable(this);
    this.setCurrentData({ ...srcData });
    this.updateTexts(undefined);
  }

  format: Format = "edit";

  setFormat(f: Format) {
    this.format = f;
    this.updateTexts(undefined);
  }

  updateTexts(prev: Format | undefined) {
    const { currentData } = this;
    if (prev !== "json") this.setJson(currentData);
    if (prev !== "yaml") this.setYaml(currentData);
  }

  currentData: unknown = null;

  setCurrentData(data: unknown) {
    this.currentData = data;
    this.updateFinalData();
  }

  finalData: ZAttrMeta | null = null;

  setFinalData(data: ZAttrMeta | null) {
    this.finalData = data;
  }

  updateFinalData() {
    try {
      this.setFinalData(null);
      const atttrMeta = zAttrMeta.parse(this.currentData);
      const availableTypes = this.attrFeatures?.availableTypes;
      if (availableTypes && !availableTypes.has(atttrMeta.type.name)) {
        throw Error(`Недопустимый тип: ${atttrMeta.type.name}`);
      }
      this.setFinalData(atttrMeta);
    } catch (e) {
      if (e instanceof ZodError) {
        this.setCurError(new Error(getZodErrorText(e)));
      } else {
        this.setCurError(e);
      }
    }
  }

  jsonText = "";

  setJsonText(text: string) {
    this.jsonText = text;
  }

  setJson(data: unknown) {
    this.setJsonText(JSON.stringify(data, null, "  "));
  }

  changeJson(text: string) {
    this.setJsonText(text);
    try {
      this.setCurError(null);
      this.setCurrentData(JSON.parse(text));
      this.updateTexts("json");
    } catch (e) {
      this.setCurError(e);
    }
  }

  changeEditor(value: unknown) {
    try {
      this.setCurError(null);
      this.setCurrentData(value);
      this.updateTexts(undefined);
    } catch (e) {
      this.setCurError(e);
    }
  }

  yamlText = "";

  setYamlText(text: string) {
    this.yamlText = text;
  }

  setYaml(data: unknown) {
    this.setYamlText(YAML.stringify(data));
  }

  changeYaml(text: string) {
    this.setYamlText(text);
    try {
      this.setCurError(null);
      this.setCurrentData(YAML.parse(text));
      this.updateTexts("yaml");
    } catch (e) {
      this.setCurError(e);
    }
  }

  curError: Error | null = null;

  setCurError(e: Error | null) {
    this.curError = e;
  }

  preset(presetName: string) {
    const res = this.currentData as ZAttrMeta;
    if (!res) return;
    let newData: ZAttrMeta | undefined;
    const def = presets.find(({ key }) => key === presetName);
    if (def) {
      newData = { ...res, ...def.code };
    }
    if (newData) {
      this.setCurrentData(newData);
      this.updateTexts(undefined);
    }
  }

  get presetItems(): MenuProps["items"] {
    const features = this.attrFeatures;
    const availableTypes = features?.availableTypes;
    const availableArray = !availableTypes || availableTypes.has("array");
    let result = presets;
    if (availableTypes) {
      result = result.filter(({ code }) => availableTypes.has(code.type.name));
    }
    if (availableArray) {
      result = result.filter(({ arrayAlt }) => !arrayAlt);
    }
    return result.map(({ key, label }) => ({ key, label }));
  }
}

export const AttrMetaEditorModal: React.FC<PropsAttrMeatEditorModal> = observer(
  (props) => {
    const { value, close, onChange, attrFeatures = {} } = props;
    const [store, setStore] = React.useState<AttrMetaStore | undefined>();
    React.useEffect(() => {
      setStore(value ? new AttrMetaStore(value, attrFeatures) : undefined);
    }, [value]);
    return (
      <Modal
        title="Изменение атрибута"
        onCancel={close}
        open={!!value}
        centered
        width="60vw"
        onOk={() => {
          if (store?.finalData) {
            onChange(store?.finalData);
            close();
          }
        }}
        okButtonProps={{ disabled: !store?.finalData }}
      >
        {!!store && (
          <AttrMetaEditor store={store} attrFeatures={attrFeatures} />
        )}
      </Modal>
    );
  },
);

type PropsAttrMetaEditor = {
  store: AttrMetaStore;
  attrFeatures: AttrFeatures;
};

const AttrMetaEditor: React.FC<PropsAttrMetaEditor> = observer((props) => {
  const { store, attrFeatures } = props;
  const tabs: TabsProps["items"] = [
    {
      key: "edit",
      label: "Editor",
    },
    {
      key: "json",
      label: "JSON",
    },
    {
      key: "yaml",
      label: "YAML",
    },
  ];
  const { curError, format, presetItems } = store;
  let extra: React.ReactNode = null;
  if (!store.srcData.name) {
    extra = (
      <Dropdown
        menu={{ items: presetItems, onClick: ({ key }) => store.preset(key) }}
        placement="bottomRight"
      >
        <Button>Пресеты</Button>
      </Dropdown>
    );
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "70vh" }}>
      <Tabs
        activeKey={store.format}
        onChange={(t) => store.setFormat(t as Format)}
        items={tabs}
        tabBarExtraContent={extra}
      />
      {format === "json" && <JsonEditor store={store} />}
      {format === "yaml" && <YamlEditor store={store} />}
      {format === "edit" && (
        <AttrMetaEditorForm
          srcValue={store.currentData}
          onChange={(v) => store.changeEditor(v)}
          attrFeatures={attrFeatures}
        />
      )}
      {curError && (
        <div style={{ color: "red" }}>{getErrorMessage(curError).message}</div>
      )}
    </div>
  );
});

type PropsJsonEditor = {
  store: AttrMetaStore;
};
const JsonEditor: React.FC<PropsJsonEditor> = observer(({ store }) => (
  <div style={{ flex: 1 }}>
    <MonacoEditor
      value={store.jsonText}
      defaultLanguage="json"
      onChange={(val) => store.changeJson(val ?? "")}
    />
  </div>
));

type PropsYamlEditor = {
  store: AttrMetaStore;
};
const YamlEditor: React.FC<PropsYamlEditor> = observer(({ store }) => (
  <div style={{ flex: 1 }}>
    <MonacoEditor
      value={store.yamlText}
      defaultLanguage="yaml"
      onChange={(val) => store.changeYaml(val ?? "")}
    />
  </div>
));
